// src/screens/Withdrawals.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../css/Withdrawals.css";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import useIdleLogout from "../hooks/useIdleLogout";

/**
 * .env REQUIRED:
 * REACT_APP_ADMIN_WITHDRAWALS_API_URL=https://grab.newedge.bt/bfs/api/admin/withdrawals
 *
 * Admin actions (under same base):
 * POST /:id/needs-info  { note }
 * POST /:id/approve     { admin_note }
 * POST /:id/mark-paid   { bank_reference, note }
 * POST /:id/reject      { reason }
 * POST /:id/fail        { reason }
 */

const API_ADMIN_WITHDRAWALS = (process.env.REACT_APP_ADMIN_WITHDRAWALS_API_URL || "").trim();
const DISPLAY_LIMIT = 20;

/* ===== helpers ===== */
const pad2 = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

function getDefaultRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: ymd(from), to: ymd(to) };
}

function safeDateMs(v) {
  if (!v) return 0;
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

const money = (n) =>
  `BTN. ${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function isDarkTheme() {
  const b = document.body;
  const el = document.documentElement;
  const cls =
    b.classList.contains("dark") ||
    b.classList.contains("dark-mode") ||
    b.classList.contains("theme-dark") ||
    el.classList.contains("dark") ||
    el.classList.contains("dark-mode") ||
    el.classList.contains("theme-dark");
  const data = (el.getAttribute("data-theme") || "").toLowerCase() === "dark";
  return cls || data;
}

function swalThemeOpts() {
  const dark = isDarkTheme();
  return {
    background: dark ? "#0f172a" : "#ffffff",
    color: dark ? "#e5e7eb" : "#0f172a",
    customClass: {
      popup: "wd-swal-popup",
      title: "wd-swal-title",
      htmlContainer: "wd-swal-html",
      closeButton: "wd-swal-close",
    },
  };
}

function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function normAdminWithdrawals(raw) {
  const arr =
    (Array.isArray(raw?.data) && raw.data) ||
    (Array.isArray(raw?.items) && raw.items) ||
    (Array.isArray(raw?.withdrawals) && raw.withdrawals) ||
    (Array.isArray(raw) && raw) ||
    [];

  return arr.map((r, i) => {
    const createdAt = r.created_at || r.createdAt || r.requested_at || r.requestedAt || "";
    const createdAtMs = safeDateMs(createdAt);

    const amount = r.amount ?? r.amount_nu ?? r.amountNu ?? 0;
    const amountNu =
      Number.isFinite(Number(amount))
        ? Number(amount)
        : Number(String(amount).replace(/,/g, "")) || 0;

    const bank = r.bank || {};
    const bank_code = r.bank_code ?? bank.bank_code ?? "";
    const bank_name = r.bank_name ?? bank.bank_name ?? "";
    const account_no = r.account_no ?? bank.account_no ?? "";
    const account_name = r.account_name ?? bank.account_name ?? "";

    return {
      id: r.request_id ?? r.id ?? i + 1,
      request_id: r.request_id ?? r.id ?? "",
      user_id: r.user_id ?? r.userId ?? "",

      status: String(r.status || "").toUpperCase(),
      amountNu,
      currency: r.currency ?? "BTN",

      bank_code,
      bank_name,
      account_no,
      account_name,

      user_note: r.user_note ?? r.note ?? "",
      admin_note: r.admin_note ?? r.review_note ?? r.note_admin ?? "",
      reason: r.reason ?? "",
      bank_reference: r.bank_reference ?? "",

      reviewed_by: r.reviewed_by ?? "",
      approved_by: r.approved_by ?? "",
      paid_by: r.paid_by ?? "",

      created_at: createdAt ? String(createdAt) : "",
      createdAtMs,
    };
  });
}

function statusBadgeClass(status) {
  const s = String(status || "").toUpperCase();
  if (s === "PAID") return "is-paid";
  if (s === "APPROVED") return "is-approved";
  if (s === "NEEDS_INFO") return "is-needs";
  if (s === "HELD") return "is-held";
  if (s === "CANCELLED") return "is-cancelled";
  if (s === "REJECTED") return "is-rejected";
  if (s === "FAILED") return "is-failed";
  return "is-neutral";
}

function actionStateForStatus(status) {
  const s = String(status || "").toUpperCase();
  return {
    canNeedsInfo: s === "HELD",
    canApprove: s === "HELD" || s === "NEEDS_INFO",
    canMarkPaid: s === "APPROVED",
    canReject: s === "HELD" || s === "NEEDS_INFO",
    canFail: s === "HELD" || s === "NEEDS_INFO" || s === "APPROVED",
  };
}

export default function Withdrawals() {
  useIdleLogout();

  const def = useMemo(() => getDefaultRange(), []);
  const [tab, setTab] = useState("ALL"); // ✅ affects table only (NOT summary)
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(def.from);
  const [toDate, setToDate] = useState(def.to);
  const [displayLimit, setDisplayLimit] = useState(DISPLAY_LIMIT);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ✅ Keep ONE master dataset for date range (ALL statuses)
  const [allRows, setAllRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("access_token") || "";

  // ✅ Dynamic admin info (ID + name)
  const adminUserId =
    Number(localStorage.getItem("user_id") || 0) ||
    Number(localStorage.getItem("admin_id") || 0) ||
    1;

  const adminUserName =
    localStorage.getItem("user_name") ||
    localStorage.getItem("name") ||
    localStorage.getItem("username") ||
    "Admin";

  const fetchAdminWithdrawals = async ({
    from,
    to,
    status, // optional
    userId,
    limit = 200,
    offset = 0,
  }) => {
    if (!API_ADMIN_WITHDRAWALS) {
      throw new Error("Missing API config: set REACT_APP_ADMIN_WITHDRAWALS_API_URL in .env and restart.");
    }

    const base = API_ADMIN_WITHDRAWALS.replace(/\/+$/, "");
    const qs = new URLSearchParams();
    if (status && status !== "ALL") qs.set("status", status);
    if (userId) qs.set("user_id", String(userId));
    if (from) qs.set("from", String(from));
    if (to) qs.set("to", String(to));
    qs.set("limit", String(limit));
    qs.set("offset", String(offset));

    const url = `${base}?${qs.toString()}`;

    const res = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`Failed to fetch withdrawals (${res.status}) ${t.slice(0, 200)}`);
    }

    const json = await res.json().catch(() => ({}));
    return normAdminWithdrawals(json);
  };

  // ✅ Fetch ALWAYS ALL for the date range (summary never changes on tab click)
  const fetchRangeAll = async () => {
    if (!fromDate || !toDate) {
      setError("Please select both start and end dates.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminWithdrawals({
        from: fromDate,
        to: toDate,
        status: "ALL", // ✅ get everything for summary + table filtering
        limit: 1000,
        offset: 0,
      });
      setAllRows(data);
    } catch (e) {
      console.error("[Withdrawals] fetch error:", e);
      setError(e?.message || String(e));
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ IMPORTANT: tab removed from dependency list
  useEffect(() => {
    const t = setTimeout(fetchRangeAll, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  // Reset display limit and selection when filters change
  useEffect(() => {
    setDisplayLimit(DISPLAY_LIMIT);
    setSelectedIds(new Set());
  }, [tab, search, fromDate, toDate]);

  /* =========================
     Summary should NOT depend on tab or search
  ========================== */
  const rangeRows = useMemo(() => {
    // safety date filter (in case backend returns extra)
    const fromMs = fromDate ? new Date(fromDate).setHours(0, 0, 0, 0) : null;
    const toMs = toDate ? new Date(toDate).setHours(23, 59, 59, 999) : null;

    return allRows.filter((r) => {
      if (fromMs && r.createdAtMs && r.createdAtMs < fromMs) return false;
      if (toMs && r.createdAtMs && r.createdAtMs > toMs) return false;
      return true;
    });
  }, [allRows, fromDate, toDate]);

  const topSummary = useMemo(() => {
    const total = rangeRows.length;
    let totalAmount = 0;

    const counts = {
      HELD: 0,
      NEEDS_INFO: 0,
      APPROVED: 0,
      PAID: 0,
      CANCELLED: 0,
      REJECTED: 0,
      FAILED: 0,
      OTHER: 0,
    };

    rangeRows.forEach((r) => {
      totalAmount += r.amountNu || 0;
      const s = String(r.status || "").toUpperCase();
      if (counts[s] != null) counts[s] += 1;
      else counts.OTHER += 1;
    });

    return { total, totalAmount, counts };
  }, [rangeRows]);

  /* =========================
     Table rows depend on tab + search
  ========================== */
  const q = search.trim().toLowerCase();

  const tableRows = useMemo(() => {
    return rangeRows.filter((r) => {
      // tab filter
      if (tab !== "ALL" && String(r.status || "").toUpperCase() !== tab) return false;

      // search filter
      if (!q) return true;

      const combined = `
        ${r.request_id} ${r.user_id} ${r.status} ${r.amountNu}
        ${r.bank_code} ${r.bank_name} ${r.account_no} ${r.account_name}
        ${r.bank_reference} ${r.user_note} ${r.admin_note} ${r.reason}
        ${r.created_at}
      `.toLowerCase();

      return combined.includes(q);
    });
  }, [rangeRows, tab, q]);

  const visibleRows = useMemo(() => tableRows.slice(0, displayLimit), [tableRows, displayLimit]);

  /* =========================
     BULK SELECTION
  ========================== */
  const approvedVisibleRows = useMemo(
    () => visibleRows.filter((r) => actionStateForStatus(r.status).canMarkPaid),
    [visibleRows]
  );
  const allVisibleApprovedSelected =
    approvedVisibleRows.length > 0 && approvedVisibleRows.every((r) => selectedIds.has(r.request_id));
  const someVisibleApprovedSelected = approvedVisibleRows.some((r) => selectedIds.has(r.request_id));

  const selectedApprovedRows = useMemo(
    () => tableRows.filter((r) => selectedIds.has(r.request_id) && actionStateForStatus(r.status).canMarkPaid),
    [tableRows, selectedIds]
  );

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allVisibleApprovedSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        approvedVisibleRows.forEach((r) => next.delete(r.request_id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        approvedVisibleRows.forEach((r) => next.add(r.request_id));
        return next;
      });
    }
  };

  const handleBulkMarkPaid = async () => {
    const rows = selectedApprovedRows;
    if (!rows.length) return;

    const inputRows = rows
      .map(
        (r, i) => `
        <tr style="border-bottom:1px solid rgba(148,163,184,.18)">
          <td style="padding:7px 8px;font-size:12px;font-variant-numeric:tabular-nums">${escapeHtml(String(r.request_id))}</td>
          <td style="padding:7px 8px;font-size:12px">${escapeHtml(String(r.user_id))}</td>
          <td style="padding:7px 8px;font-size:12px;font-variant-numeric:tabular-nums">${escapeHtml(money(r.amountNu))}</td>
          <td style="padding:7px 8px">
            <input id="wd-ref-${i}" type="text" placeholder="Journal No. *"
              style="width:130px;padding:5px 8px;border-radius:6px;border:1px solid rgba(148,163,184,.5);background:transparent;color:inherit;font-size:12px" />
          </td>
          <td style="padding:7px 8px">
            <input id="wd-note-${i}" type="text" placeholder="Note (optional)"
              style="width:120px;padding:5px 8px;border-radius:6px;border:1px solid rgba(148,163,184,.5);background:transparent;color:inherit;font-size:12px" />
          </td>
        </tr>`
      )
      .join("");

    const html = `
      <div style="margin-bottom:12px;padding:10px 12px;border-radius:10px;border:1px solid rgba(148,163,184,.3);display:flex;align-items:center;gap:10px;flex-wrap:wrap">
        <span style="font-size:13px;font-weight:700">Fill all with same journal no.:</span>
        <input id="wd-fill-all" type="text" placeholder="e.g. JNRL-20260508-001"
          style="flex:1;min-width:160px;padding:6px 10px;border-radius:6px;border:1px solid rgba(148,163,184,.5);background:transparent;color:inherit;font-size:13px" />
        <button type="button" id="wd-apply-all"
          style="padding:6px 14px;border-radius:999px;border:1px solid rgba(22,163,74,.6);background:rgba(22,163,74,.1);color:#16a34a;font-weight:700;cursor:pointer;font-size:13px">
          Apply to all
        </button>
      </div>
      <div style="overflow-x:auto;max-height:340px;overflow-y:auto">
        <table style="width:100%;border-collapse:collapse;font-size:13px">
          <thead>
            <tr style="border-bottom:2px solid rgba(148,163,184,.3)">
              <th style="padding:8px;text-align:left;white-space:nowrap">Request ID</th>
              <th style="padding:8px;text-align:left;white-space:nowrap">User ID</th>
              <th style="padding:8px;text-align:left;white-space:nowrap">Amount</th>
              <th style="padding:8px;text-align:left;white-space:nowrap">Journal No. *</th>
              <th style="padding:8px;text-align:left;white-space:nowrap">Note</th>
            </tr>
          </thead>
          <tbody>${inputRows}</tbody>
        </table>
      </div>
    `;

    const { isConfirmed, value } = await Swal.fire({
      title: `Mark ${rows.length} withdrawal${rows.length > 1 ? "s" : ""} as PAID`,
      html,
      showCancelButton: true,
      confirmButtonText: "Mark All Paid",
      width: 860,
      ...swalThemeOpts(),
      didOpen: () => {
        document.getElementById("wd-apply-all")?.addEventListener("click", () => {
          const fillVal = String(document.getElementById("wd-fill-all")?.value || "").trim();
          if (!fillVal) return;
          rows.forEach((_, i) => {
            const el = document.getElementById(`wd-ref-${i}`);
            if (el) el.value = fillVal;
          });
        });
      },
      preConfirm: () => {
        const entries = [];
        for (let i = 0; i < rows.length; i++) {
          const ref = String(document.getElementById(`wd-ref-${i}`)?.value || "").trim();
          if (!ref) {
            Swal.showValidationMessage(`Journal number is required for Request ID: ${rows[i].request_id}`);
            document.getElementById(`wd-ref-${i}`)?.focus();
            return false;
          }
          entries.push({
            request_id: rows[i].request_id,
            bank_reference: ref,
            note: String(document.getElementById(`wd-note-${i}`)?.value || "").trim() || null,
          });
        }
        return entries;
      },
    });

    if (!isConfirmed) return;

    showLoaderOnly();
    let successCount = 0;
    const errors = [];

    for (const entry of value) {
      try {
        await postAdminAction(entry.request_id, "mark-paid", {
          bank_reference: entry.bank_reference,
          note: entry.note,
        });
        successCount++;
      } catch (e) {
        errors.push(`${entry.request_id}: ${e?.message || String(e)}`);
      }
    }

    Swal.close();
    setSelectedIds(new Set());

    if (!errors.length) {
      await Swal.fire({
        icon: "success",
        title: "All Paid",
        text: `${successCount} withdrawal${successCount > 1 ? "s" : ""} marked as PAID.`,
        ...swalThemeOpts(),
      });
    } else {
      await Swal.fire({
        icon: "warning",
        title: `${successCount} paid, ${errors.length} failed`,
        html: `<div style="text-align:left;font-size:13px">${errors.map((e) => `<div style="margin-bottom:4px">• ${escapeHtml(e)}</div>`).join("")}</div>`,
        ...swalThemeOpts(),
      });
    }

    fetchRangeAll();
  };

  /* =========================
     ADMIN ACTIONS
  ========================== */
  const postAdminAction = async (requestId, action, body) => {
    const base = API_ADMIN_WITHDRAWALS.replace(/\/+$/, "");
    const url = `${base}/${encodeURIComponent(String(requestId))}/${action}`;

    const payload = {
      ...(body || {}),
      admin_id: adminUserId || 1,
      admin_name: adminUserName || "Admin",
    };

    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-admin-id": String(adminUserId || 1),
        "x-admin-name": String(adminUserName || "Admin"),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`Action failed (${res.status}): ${t.slice(0, 200)}`);
    }
    return res.json().catch(() => ({}));
  };

  const showLoaderOnly = async () => {
    await Swal.fire({
      didOpen: () => Swal.showLoading(),
      allowOutsideClick: false,
      showConfirmButton: false,
      showCloseButton: false,
      background: "transparent",
      color: "transparent",
      padding: 0,
      width: "auto",
      customClass: { popup: "wd-swal-loader-only" },
    });
  };

  const handleNeedsInfo = async (r) => {
    const s = actionStateForStatus(r.status);
    if (!s.canNeedsInfo) return;

    const { value, isConfirmed } = await Swal.fire({
      title: "Mark as NEEDS INFO",
      html: `
        <div style="text-align:left">
          <div><b>Request ID:</b> ${escapeHtml(r.request_id)}</div>
          <div style="margin-top:6px"><b>User ID:</b> ${escapeHtml(r.user_id)}</div>
          <div style="margin-top:6px"><b>Amount:</b> ${escapeHtml(money(r.amountNu))}</div>
          <div style="margin-top:10px;opacity:.7"><b>Admin:</b> ${escapeHtml(adminUserName)} (ID: ${escapeHtml(adminUserId)})</div>
          <div style="margin-top:12px"><b>Note (required):</b></div>
        </div>
      `,
      input: "textarea",
      inputPlaceholder: "e.g., Please confirm account name as per bank record",
      showCancelButton: true,
      confirmButtonText: "Save",
      ...swalThemeOpts(),
      preConfirm: (v) => {
        const note = String(v || "").trim();
        if (!note) {
          Swal.showValidationMessage("Note is required.");
          return false;
        }
        return note;
      },
    });

    if (!isConfirmed) return;

    showLoaderOnly();
    try {
      await postAdminAction(r.request_id, "needs-info", { note: value });
      Swal.close();
      await Swal.fire({ icon: "success", title: "Updated", text: "Status set to NEEDS_INFO.", ...swalThemeOpts() });
      fetchRangeAll();
    } catch (e) {
      Swal.close();
      await Swal.fire({ icon: "error", title: "Failed", text: e?.message || String(e), ...swalThemeOpts() });
    }
  };

  const handleApprove = async (r) => {
    const s = actionStateForStatus(r.status);
    if (!s.canApprove) return;

    const { value, isConfirmed } = await Swal.fire({
      title: "Approve withdrawal",
      html: `
        <div style="text-align:left">
          <div><b>Request ID:</b> ${escapeHtml(r.request_id)}</div>
          <div style="margin-top:6px"><b>User ID:</b> ${escapeHtml(r.user_id)}</div>
          <div style="margin-top:6px"><b>Amount:</b> ${escapeHtml(money(r.amountNu))}</div>
          <div style="margin-top:10px;opacity:.7"><b>Admin:</b> ${escapeHtml(adminUserName)} (ID: ${escapeHtml(adminUserId)})</div>
          <div style="margin-top:12px"><b>Admin note (optional):</b></div>
        </div>
      `,
      input: "textarea",
      inputPlaceholder: "e.g., Verified wallet + bank details",
      showCancelButton: true,
      confirmButtonText: "Approve",
      ...swalThemeOpts(),
    });

    if (!isConfirmed) return;

    showLoaderOnly();
    try {
      await postAdminAction(r.request_id, "approve", {
        admin_note: String(value || "").trim() || null,
      });
      Swal.close();
      await Swal.fire({ icon: "success", title: "Approved", text: "Status set to APPROVED.", ...swalThemeOpts() });
      fetchRangeAll();
    } catch (e) {
      Swal.close();
      await Swal.fire({ icon: "error", title: "Failed", text: e?.message || String(e), ...swalThemeOpts() });
    }
  };

  const handleMarkPaid = async (r) => {
    const s = actionStateForStatus(r.status);
    if (!s.canMarkPaid) return;

    const { value, isConfirmed } = await Swal.fire({
      title: "Mark as PAID",
      html: `
        <div style="text-align:left">
          <div><b>Request ID:</b> ${escapeHtml(r.request_id)}</div>
          <div style="margin-top:6px"><b>User ID:</b> ${escapeHtml(r.user_id)}</div>
          <div style="margin-top:6px"><b>Amount:</b> ${escapeHtml(money(r.amountNu))}</div>
          <div style="margin-top:10px;opacity:.7"><b>Admin:</b> ${escapeHtml(adminUserName)} (ID: ${escapeHtml(adminUserId)})</div>
          <div style="margin-top:12px"><b>Bank Reference (required):</b></div>
        </div>
      `,
      input: "text",
      inputPlaceholder: "e.g., JNRL Number",
      showCancelButton: true,
      confirmButtonText: "Mark Paid",
      ...swalThemeOpts(),
      preConfirm: (v) => {
        const ref = String(v || "").trim();
        if (!ref) {
          Swal.showValidationMessage("Bank reference is required.");
          return false;
        }
        return ref;
      },
    });

    if (!isConfirmed) return;

    const noteRes = await Swal.fire({
      title: "Payment note (optional)",
      input: "textarea",
      inputPlaceholder: "e.g., Paid via bank transfer",
      showCancelButton: true,
      confirmButtonText: "Continue",
      ...swalThemeOpts(),
    });

    if (noteRes.isDismissed) return;

    showLoaderOnly();
    try {
      await postAdminAction(r.request_id, "mark-paid", {
        bank_reference: value,
        note: String(noteRes.value || "").trim() || null,
      });
      Swal.close();
      await Swal.fire({ icon: "success", title: "Paid", text: "Status set to PAID.", ...swalThemeOpts() });
      fetchRangeAll();
    } catch (e) {
      Swal.close();
      await Swal.fire({ icon: "error", title: "Failed", text: e?.message || String(e), ...swalThemeOpts() });
    }
  };

  const handleReject = async (r) => {
    const s = actionStateForStatus(r.status);
    if (!s.canReject) return;

    const { value, isConfirmed } = await Swal.fire({
      title: "Reject withdrawal",
      html: `
        <div style="text-align:left">
          <div><b>Request ID:</b> ${escapeHtml(r.request_id)}</div>
          <div style="margin-top:6px"><b>User ID:</b> ${escapeHtml(r.user_id)}</div>
          <div style="margin-top:6px"><b>Amount:</b> ${escapeHtml(money(r.amountNu))}</div>
          <div style="margin-top:10px;opacity:.7"><b>Admin:</b> ${escapeHtml(adminUserName)} (ID: ${escapeHtml(adminUserId)})</div>
          <div style="margin-top:12px"><b>Reason (required):</b></div>
        </div>
      `,
      input: "textarea",
      inputPlaceholder: "e.g., Account name mismatch",
      showCancelButton: true,
      confirmButtonText: "Reject",
      ...swalThemeOpts(),
      preConfirm: (v) => {
        const reason = String(v || "").trim();
        if (!reason) {
          Swal.showValidationMessage("Reason is required.");
          return false;
        }
        return reason;
      },
    });

    if (!isConfirmed) return;

    showLoaderOnly();
    try {
      await postAdminAction(r.request_id, "reject", { reason: value });
      Swal.close();
      await Swal.fire({ icon: "success", title: "Rejected", text: "Status set to REJECTED (refund expected).", ...swalThemeOpts() });
      fetchRangeAll();
    } catch (e) {
      Swal.close();
      await Swal.fire({ icon: "error", title: "Failed", text: e?.message || String(e), ...swalThemeOpts() });
    }
  };

  const handleFail = async (r) => {
    const s = actionStateForStatus(r.status);
    if (!s.canFail) return;

    const { value, isConfirmed } = await Swal.fire({
      title: "Mark as FAILED",
      html: `
        <div style="text-align:left">
          <div><b>Request ID:</b> ${escapeHtml(r.request_id)}</div>
          <div style="margin-top:6px"><b>User ID:</b> ${escapeHtml(r.user_id)}</div>
          <div style="margin-top:6px"><b>Amount:</b> ${escapeHtml(money(r.amountNu))}</div>
          <div style="margin-top:10px;opacity:.7"><b>Admin:</b> ${escapeHtml(adminUserName)} (ID: ${escapeHtml(adminUserId)})</div>
          <div style="margin-top:12px"><b>Reason (required):</b></div>
        </div>
      `,
      input: "textarea",
      inputPlaceholder: "e.g., Bank transfer failed",
      showCancelButton: true,
      confirmButtonText: "Mark Failed",
      ...swalThemeOpts(),
      preConfirm: (v) => {
        const reason = String(v || "").trim();
        if (!reason) {
          Swal.showValidationMessage("Reason is required.");
          return false;
        }
        return reason;
      },
    });

    if (!isConfirmed) return;

    showLoaderOnly();
    try {
      await postAdminAction(r.request_id, "fail", { reason: value });
      Swal.close();
      await Swal.fire({ icon: "success", title: "Failed", text: "Status set to FAILED (refund expected).", ...swalThemeOpts() });
      fetchRangeAll();
    } catch (e) {
      Swal.close();
      await Swal.fire({ icon: "error", title: "Failed", text: e?.message || String(e), ...swalThemeOpts() });
    }
  };

  const handleViewDetails = async (r) => {
    const html = `
      <div style="text-align:left">
        <div style="display:flex;gap:10px;flex-wrap:wrap;align-items:center;">
          <div><b>Status:</b> <span class="wd-inline-badge ${statusBadgeClass(r.status)}">${escapeHtml(r.status || "—")}</span></div>
          <div style="opacity:.7;">•</div>
          <div><b>Amount:</b> ${escapeHtml(money(r.amountNu))}</div>
        </div>

        <div style="margin-top:10px;opacity:.7"><b>Admin:</b> ${escapeHtml(adminUserName)} (ID: ${escapeHtml(adminUserId)})</div>

        <div style="margin-top:12px;display:grid;grid-template-columns:160px 1fr;gap:8px;">
          <div style="opacity:.75"><b>Request ID</b></div><div>${escapeHtml(r.request_id)}</div>
          <div style="opacity:.75"><b>User ID</b></div><div>${escapeHtml(r.user_id)}</div>
          <div style="opacity:.75"><b>Created At</b></div><div>${escapeHtml(r.created_at || "—")}</div>

          <div style="opacity:.75"><b>Bank Code</b></div><div>${escapeHtml(r.bank_code || "—")}</div>
          <div style="opacity:.75"><b>Bank Name</b></div><div>${escapeHtml(r.bank_name || "—")}</div>
          <div style="opacity:.75"><b>Account No</b></div><div>${escapeHtml(r.account_no || "—")}</div>
          <div style="opacity:.75"><b>Account Name</b></div><div>${escapeHtml(r.account_name || "—")}</div>

          <div style="opacity:.75"><b>Bank Ref</b></div><div>${escapeHtml(r.bank_reference || "—")}</div>
          <div style="opacity:.75"><b>User Note</b></div><div>${escapeHtml(r.user_note || "—")}</div>
          <div style="opacity:.75"><b>Admin Note</b></div><div>${escapeHtml(r.admin_note || "—")}</div>
          <div style="opacity:.75"><b>Reason</b></div><div>${escapeHtml(r.reason || "—")}</div>
        </div>
      </div>
    `;

    await Swal.fire({
      title: "Withdrawal Details",
      html,
      showCloseButton: true,
      showConfirmButton: false,
      width: 860,
      ...swalThemeOpts(),
    });
  };

  /* =========================
     EXPORT (fetch ALL from DB; respects tab + search)
  ========================== */
  const fetchAllForExport = async () => {
    const all = [];
    const limit = 500;
    let offset = 0;

    while (true) {
      const chunk = await fetchAdminWithdrawals({
        from: fromDate,
        to: toDate,
        status: tab, // ✅ export matches selected tab
        limit,
        offset,
      });

      if (!chunk.length) break;

      all.push(...chunk);
      offset += chunk.length;

      if (offset > 200000) break;
      if (chunk.length < limit) break;
    }

    if (!q) return all;

    return all.filter((r) => {
      const combined = `
        ${r.request_id} ${r.user_id} ${r.status} ${r.amountNu}
        ${r.bank_code} ${r.bank_name} ${r.account_no} ${r.account_name}
        ${r.bank_reference} ${r.user_note} ${r.admin_note} ${r.reason}
        ${r.created_at}
      `.toLowerCase();
      return combined.includes(q);
    });
  };

  const handleExport = async () => {
    if (!API_ADMIN_WITHDRAWALS) {
      await Swal.fire({
        icon: "error",
        title: "Missing API",
        text: "Set REACT_APP_ADMIN_WITHDRAWALS_API_URL in .env and restart.",
        ...swalThemeOpts(),
      });
      return;
    }

    const now = new Date();
    const exportedAt = now.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

    showLoaderOnly();
    try {
      const all = await fetchAllForExport();
      Swal.close();

      if (!all.length) {
        await Swal.fire({
          icon: "info",
          title: "Nothing to export",
          text: "No rows found for current filters.",
          ...swalThemeOpts(),
        });
        return;
      }

      const sheetTitle = tab === "ALL" ? "Withdrawals (All)" : `Withdrawals (${tab})`;

      const data = [
        [sheetTitle],
        [
          `Exported: ${exportedAt} (range: ${fromDate || "—"} to ${toDate || "—"}, status: ${tab || "ALL"}, search: ${
            search ? `"${search}"` : "—"
          })`,
        ],
        [`Exported by: ${adminUserName} (ID: ${adminUserId})`],
        [],
        [
          "SI.NO",
          "REQUEST ID",
          "USER ID",
          "STATUS",
          "AMOUNT (Nu)",
          "BANK CODE",
          "BANK NAME",
          "ACCOUNT NO",
          "ACCOUNT NAME",
          "BANK REF",
          "USER NOTE",
          "ADMIN NOTE",
          "REASON",
          "CREATED AT",
        ],
        ...all.map((r, idx) => [
          idx + 1,
          r.request_id || "—",
          r.user_id || "—",
          r.status || "—",
          Number(r.amountNu ?? 0),
          r.bank_code || "—",
          r.bank_name || "—",
          r.account_no || "—",
          r.account_name || "—",
          r.bank_reference || "",
          r.user_note || "",
          r.admin_note || "",
          r.reason || "",
          r.createdAtMs ? new Date(r.createdAtMs).toLocaleString() : r.created_at || "—",
        ]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(data);
      ws["!cols"] = [
        { wch: 8 },
        { wch: 22 },
        { wch: 10 },
        { wch: 14 },
        { wch: 14 },
        { wch: 10 },
        { wch: 18 },
        { wch: 16 },
        { wch: 18 },
        { wch: 18 },
        { wch: 26 },
        { wch: 26 },
        { wch: 22 },
        { wch: 22 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Withdrawals");
      XLSX.writeFile(wb, `withdrawals_${tab || "ALL"}_${fromDate}_to_${toDate}.xlsx`);
    } catch (e) {
      Swal.close();
      await Swal.fire({
        icon: "error",
        title: "Export failed",
        text: e?.message || String(e),
        ...swalThemeOpts(),
      });
    }
  };

  return (
    <div className="withdrawals-page">
      <div className="withdrawals-card">
        {/* ✅ SUMMARY ALWAYS FOR DATE RANGE (ALL STATUSES) */}
        <div className="wd-pills">
          <span className="wd-pill">
            Total requests: <b>{topSummary.total}</b>
          </span>
          <span className="wd-pill">
            Total amount: <b>{money(topSummary.totalAmount)}</b>
          </span>
          <span className="wd-pill">
            Held: <b>{topSummary.counts.HELD}</b>
          </span>
          <span className="wd-pill">
            Needs info: <b>{topSummary.counts.NEEDS_INFO}</b>
          </span>
          <span className="wd-pill">
            Approved: <b>{topSummary.counts.APPROVED}</b>
          </span>
          <span className="wd-pill">
            Paid: <b>{topSummary.counts.PAID}</b>
          </span>
          <span className="wd-pill">
            Cancelled: <b>{topSummary.counts.CANCELLED}</b>
          </span>
          <span className="wd-pill">
            Rejected: <b>{topSummary.counts.REJECTED}</b>
          </span>
          <span className="wd-pill">
            Failed: <b>{topSummary.counts.FAILED}</b>
          </span>
        </div>

        {/* ===== STATUS + DATE ROW ===== */}
        <div className="wd-status-row">
          <div className="wd-tabs" role="tablist" aria-label="Withdrawals Status Tabs">
            {["ALL", "HELD", "NEEDS_INFO", "APPROVED", "PAID", "CANCELLED", "REJECTED", "FAILED"].map((s) => (
              <button
                key={s}
                type="button"
                className={`wd-tab ${tab === s ? "active" : ""}`}
                onClick={() => setTab(s)}
              >
                {s === "NEEDS_INFO" ? "NEEDS INFO" : s}
              </button>
            ))}
          </div>

          <div className="wd-date-group">
            <div className="withdrawals-date-pill">
              <input
                type="date"
                className="withdrawals-input withdrawals-input-date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <span className="withdrawals-to-label">to</span>
            <div className="withdrawals-date-pill">
              <input
                type="date"
                className="withdrawals-input withdrawals-input-date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ===== SEARCH ===== */}
        <div className="withdrawals-toolbar">
          <div className="withdrawals-search">
            <span className="withdrawals-search-icon bi bi-search" aria-hidden="true" />
            <input
              type="text"
              className="withdrawals-input withdrawals-input-search"
              placeholder="Search request id, user id, bank, account, status, reference..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {error && <div className="alert alert-danger mb-2">{error}</div>}

        {/* ===== BULK ACTION BAR ===== */}
        {selectedApprovedRows.length > 0 && (
          <div className="wd-bulk-bar">
            <span className="wd-bulk-info">
              <i className="bi bi-check2-square" /> {selectedApprovedRows.length} APPROVED row{selectedApprovedRows.length > 1 ? "s" : ""} selected
            </span>
            <button
              type="button"
              className="wd-bulk-paid-btn"
              onClick={handleBulkMarkPaid}
            >
              <i className="bi bi-bank" /> Mark All Selected as Paid
            </button>
            <button
              type="button"
              className="wd-bulk-clear-btn"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </button>
          </div>
        )}

        {/* ===== TABLE ===== */}
        <div className="withdrawals-table-wrapper">
          <table className="withdrawals-table">
            <thead>
              <tr>
                <th className="col-cb">
                  {approvedVisibleRows.length > 0 && (
                    <input
                      type="checkbox"
                      className="wd-checkbox"
                      checked={allVisibleApprovedSelected}
                      ref={(el) => { if (el) el.indeterminate = someVisibleApprovedSelected && !allVisibleApprovedSelected; }}
                      onChange={toggleSelectAll}
                      title="Select all visible APPROVED rows"
                    />
                  )}
                </th>
                <th className="col-si">SI.NO</th>
                <th className="col-id">REQUEST ID</th>
                <th className="col-id">USER ID</th>
                <th className="col-status">STATUS</th>
                <th className="col-money">AMOUNT</th>
                <th className="col-bank">BANK</th>
                <th className="col-bank">ACCOUNT</th>
                <th className="col-date">CREATED AT</th>
                <th className="col-action">ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={10} className="withdrawals-loading">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && !visibleRows.length && (
                <tr>
                  <td colSpan={10} className="withdrawals-empty">
                    No data found for the current filters.
                  </td>
                </tr>
              )}

              {!loading &&
                visibleRows.map((r, idx) => {
                  const s = actionStateForStatus(r.status);

                  return (
                    <tr key={r.request_id || idx} className={selectedIds.has(r.request_id) ? "wd-row-selected" : ""}>
                      <td className="col-cb">
                        {s.canMarkPaid && (
                          <input
                            type="checkbox"
                            className="wd-checkbox"
                            checked={selectedIds.has(r.request_id)}
                            onChange={() => toggleSelect(r.request_id)}
                          />
                        )}
                      </td>
                      <td>{idx + 1}</td>

                      <td className="mono">{r.request_id || "—"}</td>
                      <td className="mono">{r.user_id || "—"}</td>

                      <td>
                        <span className={`wd-badge ${statusBadgeClass(r.status)}`}>
                          {r.status || "—"}
                        </span>
                      </td>

                      <td className="mono">{money(r.amountNu)}</td>

                      <td>
                        <div className="wd-stack">
                          <div className="wd-strong">{r.bank_name || "—"}</div>
                          <div className="wd-muted">{r.bank_code || "—"}</div>
                        </div>
                      </td>

                      <td>
                        <div className="wd-stack">
                          <div className="wd-strong">{r.account_name || "—"}</div>
                          <div className="wd-muted mono">{r.account_no || "—"}</div>
                        </div>
                      </td>

                      <td>
                        <small className="text-muted">
                          {r.createdAtMs
                            ? new Date(r.createdAtMs).toLocaleString()
                            : r.created_at || "—"}
                        </small>
                      </td>

                      <td className="col-action">
                        <div className="wd-actions">
                          <button
                            type="button"
                            className="wd-action-btn is-neutral"
                            onClick={() => handleViewDetails(r)}
                            title="View details"
                            aria-label="View details"
                          >
                            <i className="bi bi-eye-fill" />
                          </button>

                          <button
                            type="button"
                            className="wd-action-btn is-warning"
                            disabled={!s.canNeedsInfo}
                            onClick={() => handleNeedsInfo(r)}
                            title="Mark NEEDS INFO"
                            aria-label="Mark needs info"
                          >
                            <i className="bi bi-chat-left-text-fill" />
                          </button>

                          <button
                            type="button"
                            className="wd-action-btn is-approved"
                            disabled={!s.canApprove}
                            onClick={() => handleApprove(r)}
                            title="Approve"
                            aria-label="Approve"
                          >
                            <i className="bi bi-check2-circle" />
                          </button>

                          <button
                            type="button"
                            className="wd-action-btn is-paid"
                            disabled={!s.canMarkPaid}
                            onClick={() => handleMarkPaid(r)}
                            title="Mark PAID"
                            aria-label="Mark paid"
                          >
                            <i className="bi bi-bank" />
                          </button>

                          <button
                            type="button"
                            className="wd-action-btn is-danger"
                            disabled={!s.canReject}
                            onClick={() => handleReject(r)}
                            title="Reject (refund)"
                            aria-label="Reject"
                          >
                            <i className="bi bi-x-circle" />
                          </button>

                          <button
                            type="button"
                            className="wd-action-btn is-danger"
                            disabled={!s.canFail}
                            onClick={() => handleFail(r)}
                            title="Fail (refund)"
                            aria-label="Fail"
                          >
                            <i className="bi bi-exclamation-triangle-fill" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Show more / row count */}
        {!loading && tableRows.length > displayLimit && (
          <div className="wd-table-endbar">
            <button
              type="button"
              className="withdrawals-export-btn"
              onClick={() => setDisplayLimit((prev) => prev + DISPLAY_LIMIT)}
            >
              Show more ({displayLimit} of {tableRows.length})
            </button>
          </div>
        )}

        {/* ✅ Export button at END of table */}
        <div className="wd-table-endbar">
          <button
            type="button"
            className="withdrawals-export-btn"
            onClick={handleExport}
            disabled={loading}
            title="Export Excel (ALL matching rows from DB)"
          >
            Export Excel
          </button>
        </div>

      
      </div>

      <footer className="dashboard-footer">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ།</strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}
